import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowLeft, FileText, X, Check } from "lucide-react";
import { getPendingRoomRequests, respondToRoomRequest } from "../../services/roomService";
import Loader from "../../components/common/Loader";
import Card from "../../components/common/Card";
import EmptyState from "../../components/common/EmptyState";
import ConfirmModal from "../../components/common/ConfirmModal";
import { color, radius } from "../../styles/theme";

/**
 * Pending @Room join requests (private room verification) — ported from
 * mobile's pending-approval screen (src/Screens/pending-approval). Distinct
 * from Phase 5's Access Requests, which are host invites, not room-join
 * verification requests.
 */
const PendingRoomRequests = () => {
  const { t } = useTranslation(["rooms", "common"]);
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const room = state?.room;
  const eventId = room?.EventId || room?.Id || roomId;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = () => {
    setLoading(true);
    getPendingRoomRequests(eventId)
      .then((res) => setRequests(Array.isArray(res.data) ? res.data : res.data?.Result || []))
      .catch(() => toast.error(t("pendingRoomRequests.toasts.loadFailed")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleApprove = async (item) => {
    setActingId(item.Id);
    try {
      const res = await respondToRoomRequest(item.Id, "approve");
      toast.success(res.data?.Message || t("pendingRoomRequests.toasts.approved"));
      setRequests((prev) => prev.filter((r) => r.Id !== item.Id));
    } catch (err) {
      toast.error(err.response?.data?.Message || t("pendingRoomRequests.toasts.approveFailed"));
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActingId(rejectTarget.Id);
    try {
      const res = await respondToRoomRequest(rejectTarget.Id, "reject", { message: rejectReason.trim() });
      toast.success(res.data?.Message || t("pendingRoomRequests.toasts.rejected"));
      setRequests((prev) => prev.filter((r) => r.Id !== rejectTarget.Id));
      setRejectTarget(null);
      setRejectReason("");
    } catch (err) {
      toast.error(err.response?.data?.Message || t("pendingRoomRequests.toasts.rejectFailed"));
    } finally {
      setActingId(null);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <button type="button" className="btn btn-ghost btn-sm" style={s.linkBtn} onClick={() => navigate(`/rooms/${roomId}/settings`, { state: { room } })}>
        <ArrowLeft size={13} strokeWidth={2.25} /> {t("pendingRoomRequests.backButton")}
      </button>
      <h2 style={s.title}>{t("pendingRoomRequests.title")}</h2>

      {requests.length === 0 ? (
        <EmptyState text={t("pendingRoomRequests.emptyText")} />
      ) : (
        <div style={s.list}>
          {requests.map((item) => {
            const docs = [item.ImageUrl, item.ImageUrl2, item.DocumentUrl].filter(Boolean);
            return (
              <Card key={item.Id} style={s.card}>
                <div style={s.row}><span style={s.label}>{t("pendingRoomRequests.nameLabel")}</span> {item.FirstName} {item.LastName}</div>
                {item.Email && <div style={s.row}><span style={s.label}>{t("pendingRoomRequests.emailLabel")}</span> {item.Email}</div>}
                {item.Phone && <div style={s.row}><span style={s.label}>{t("pendingRoomRequests.phoneLabel")}</span> {item.Phone}</div>}

                {docs.length > 0 && (
                  <div style={s.docsRow}>
                    {docs.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" style={s.docThumbLink}>
                        {url.toLowerCase().endsWith(".pdf") ? (
                          <div style={s.pdfThumb}><FileText size={16} strokeWidth={2} /> {t("pendingRoomRequests.pdfLabel")}</div>
                        ) : (
                          <img src={url} alt="" style={s.docThumb} />
                        )}
                      </a>
                    ))}
                  </div>
                )}

                {item.IsInfoRequested ? (
                  <p style={s.pendingInfoNote}>{t("pendingRoomRequests.infoRequestedNote")}</p>
                ) : (
                  <div style={s.actions}>
                    <button type="button" className="btn btn-danger-ghost" style={{ ...s.actionBtn, border: "1.5px solid #fecaca" }} onClick={() => { setRejectReason(""); setRejectTarget(item); }} disabled={actingId === item.Id}>
                      <X size={14} strokeWidth={2.25} /> {t("pendingRoomRequests.rejectButton")}
                    </button>
                    <button type="button" className="btn btn-primary" style={{ ...s.actionBtn, background: color.success500 }} onClick={() => handleApprove(item)} disabled={actingId === item.Id}>
                      {actingId === item.Id ? t("common:actions.pleaseWait") : <><Check size={14} strokeWidth={2.5} /> {t("pendingRoomRequests.approveButton")}</>}
                    </button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {rejectTarget && (
        <ConfirmModal
          title={t("pendingRoomRequests.confirmReject.title")}
          message={t("pendingRoomRequests.confirmReject.message", { name: `${rejectTarget.FirstName} ${rejectTarget.LastName}` })}
          confirmText={t("pendingRoomRequests.confirmReject.confirmText")}
          danger
          loading={actingId === rejectTarget.Id}
          onConfirm={handleReject}
          onCancel={() => actingId !== rejectTarget.Id && setRejectTarget(null)}
        >
          <label style={s.reasonLabel}>
            {t("pendingRoomRequests.confirmReject.reasonLabel")}
            <textarea
              aria-label={t("pendingRoomRequests.confirmReject.reasonLabel")}
              placeholder={t("pendingRoomRequests.confirmReject.reasonPlaceholder")}
              maxLength={2000}
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={actingId === rejectTarget.Id}
              style={s.reasonInput}
            />
          </label>
        </ConfirmModal>
      )}
    </div>
  );
};

const s = {
  linkBtn: { color: color.gray600, fontWeight: 600, marginBottom: 8, padding: "6px 10px" },
  title: { fontSize: 19, fontWeight: 800, color: color.textPrimary, marginBottom: 18, letterSpacing: -0.3 },
  list: { display: "flex", flexDirection: "column", gap: 14, maxWidth: 560 },
  card: { padding: 18 },
  row: { fontSize: 13, marginBottom: 6, color: color.gray800 },
  label: { color: color.textMuted, fontWeight: 600, marginRight: 4 },
  docsRow: { display: "flex", gap: 10, marginTop: 10, marginBottom: 10, flexWrap: "wrap" },
  docThumbLink: { display: "block", borderRadius: radius.sm, overflow: "hidden", border: `1px solid ${color.border}` },
  docThumb: { width: 90, height: 90, objectFit: "cover", display: "block" },
  pdfThumb: {
    width: 90, height: 90, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 4, background: color.primary50, fontSize: 12, fontWeight: 600, color: color.primary600,
  },
  pendingInfoNote: { fontSize: 12, color: "#b45309", fontStyle: "italic", margin: 0 },
  actions: { display: "flex", gap: 10, marginTop: 12 },
  actionBtn: { flex: 1, justifyContent: "center" },
  reasonLabel: { display: "block", marginTop: 12, fontSize: 12.5, fontWeight: 600, color: color.gray700 },
  reasonInput: { display: "block", width: "100%", marginTop: 6, fontSize: 13, fontFamily: "inherit", resize: "vertical" },
};

export default PendingRoomRequests;
